import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApi, useDirectory, useClientOptions } from "@/hooks/useApi";
import { PageHeader, FilterBar, DataTable, StatusBadge, ErrorBox } from "@/components/common/ui-bits";
import { FormDialog } from "@/components/common/FormDialog";
import { fmtDate, optionList, TAX_STATUSES } from "@/lib/format";

export default function TaxCases() {
  const [f, setF] = useState({});
  const { data, loading, error, reload } = useApi("/tax-cases", f);
  const { data: services } = useApi("/services", { category: "Taxes" });
  const { options: staff, name } = useDirectory();
  const clients = useClientOptions();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div>
      <PageHeader title="Servicios de taxes" subtitle="Casos fiscales, documentos y checklists. Sin cálculos automáticos ni presentación electrónica."
        actions={<Button onClick={() => setOpen(true)} data-testid="new-tax-case-button"><Plus className="mr-2 h-4 w-4" />Nuevo caso</Button>} />
      <FilterBar values={f} onChange={setF} testid="tax-filters" filters={[
        { key: "status", label: "Estado", options: optionList(TAX_STATUSES) }, { key: "client_id", label: "Cliente", options: clients }]} />
      <ErrorBox text={error} />
      <DataTable testid="tax-table" loading={loading} rows={data} onRowClick={(r) => navigate(`/taxes/${r.id}`)} empty="No hay casos fiscales."
        columns={[
          { key: "number", label: "Número", render: (r) => <span className="num text-xs">{r.number}</span> },
          { key: "title", label: "Caso", render: (r) => <span className="font-medium text-slate-900">{r.title}<span className="block text-xs font-normal text-slate-500">{r.client_name}</span></span> },
          { key: "tax_year", label: "Año" },
          { key: "docs", label: "Documentos", render: (r) => { const p = (r.documents || []).filter((d) => d.status === "Pendiente").length; return <span className={p ? "text-amber-700" : "text-emerald-700"}>{p ? `${p} pendiente(s)` : "Completos"}</span>; } },
          { key: "responsible_id", label: "Responsable", render: (r) => name(r.responsible_id) },
          { key: "created_at", label: "Creado", render: (r) => fmtDate(r.created_at) },
          { key: "status", label: "Estado", render: (r) => <StatusBadge status={r.status} /> },
        ]} />
      <FormDialog open={open} onOpenChange={setOpen} title="Nuevo caso fiscal" testid="tax-form"
        fields={[
          { name: "client_id", label: "Cliente", type: "select", options: clients, required: true },
          { name: "title", label: "Título del caso", required: true, minLength: 3 },
          { name: "service_id", label: "Servicio fiscal", type: "select", options: (services || []).map((s) => ({ value: s.id, label: s.name })), help: "Copia los documentos requeridos del servicio." },
          { name: "tax_year", label: "Año fiscal" },
          { name: "responsible_id", label: "Responsable", type: "select", options: staff },
          { name: "status", label: "Estado", type: "select", options: optionList(TAX_STATUSES), default: "Consulta inicial", required: true },
        ]}
        onSubmit={async (v) => { const r = await api.post("/tax-cases", v); toast.success("Caso creado"); reload(); navigate(`/taxes/${r.data.id}`); }} />
    </div>
  );
}
